// Types and schemas for Admin IAM Dashboard (JavaScript version)

// Mock data structure definitions
export const PERMISSION_KEYS = [
  'users.read',
  'users.write',
  'groups.read', 
  'groups.write',
  'permissions.read',
  'permissions.write',
  'permissions.manage'
];

// Default fixtures data
export const DEFAULT_PERMISSIONS = [
  { id: 'p-1', key: 'users.read', description: 'View users' },
  { id: 'p-2', key: 'users.write', description: 'Create and edit users' },
  { id: 'p-3', key: 'groups.read', description: 'View groups' },
  { id: 'p-4', key: 'groups.write', description: 'Create and edit groups' },
  { id: 'p-5', key: 'permissions.read', description: 'View permissions' },
  { id: 'p-6', key: 'permissions.write', description: 'Create and edit permissions' },
  { id: 'p-7', key: 'permissions.manage', description: 'Full permissions management' }
];

export const DEFAULT_GROUPS = [
  {
    id: 'g-admin',
    name: 'Administrators',
    description: 'Full access to all features',
    permissions: ['*'],
    members: ['u-1']
  },
  {
    id: 'g-readonly',
    name: 'ReadOnly',
    description: 'Can view lists but cannot make changes',
    permissions: ['users.read', 'groups.read', 'permissions.read'],
    members: ['u-2']
  },
  {
    id: 'g-managers',
    name: 'Managers',
    description: 'Can manage users and groups',
    permissions: ['users.read', 'users.write', 'groups.read', 'groups.write'],
    members: []
  }
];

export const DEFAULT_USERS = [
  {
    id: 'u-1',
    email: 'admin@avdcare.com',
    displayName: 'System Administrator',
    isActive: true,
    groups: ['g-admin']
  },
  {
    id: 'u-2',
    email: 'viewer@avdcare.com',
    displayName: 'Read Only User',
    isActive: true,
    groups: ['g-readonly']
  },
  {
    id: 'u-3',
    email: 'manager@avdcare.com',
    displayName: 'Department Manager',
    isActive: true,
    groups: ['g-managers']
  },
  {
    id: 'u-4',
    email: 'inactive@avdcare.com',
    displayName: 'Inactive User',
    isActive: false,
    groups: []
  }
];

// Validation functions (replacing Zod schemas)
export const validatePermissionKey = (key) => {
  if (!key) return { valid: false, error: 'Permission key is required' };
  if (!/^[a-z]+(\\.[a-z]+)*$/.test(key)) {
    return { valid: false, error: 'Permission key must be lowercase with dots' };
  }
  return { valid: true };
};

export const validateEmail = (email) => {
  if (!email) return { valid: false, error: 'Email is required' };
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return { valid: false, error: 'Invalid email address' };
  }
  return { valid: true };
};

export const validateDisplayName = (name) => {
  if (!name || name.trim().length === 0) {
    return { valid: false, error: 'Display name is required' };
  }
  if (name.length > 100) {
    return { valid: false, error: 'Display name must be under 100 characters' };
  }
  return { valid: true };
};

export const validateGroupName = (name) => {
  if (!name || name.trim().length === 0) {
    return { valid: false, error: 'Group name is required' };
  }
  if (name.length > 50) {
    return { valid: false, error: 'Group name must be under 50 characters' };
  }
  return { valid: true };
};

export const validateDescription = (description) => {
  if (description && description.length > 500) {
    return { valid: false, error: 'Description must be under 500 characters' };
  }
  return { valid: true };
};

// Helper functions
export const generateId = (prefix = '') => {
  return `${prefix}${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

export const hasPermission = (userPermissions, requiredPermission) => {
  if (!userPermissions || !Array.isArray(userPermissions)) return false;
  if (userPermissions.includes('*')) return true;
  return userPermissions.includes(requiredPermission);
};

export const hasAnyPermission = (userPermissions, requiredPermissions) => {
  if (!requiredPermissions || !Array.isArray(requiredPermissions)) return false;
  return requiredPermissions.some(permission => hasPermission(userPermissions, permission));
};

export const getUserEffectivePermissions = (user, groups) => {
  if (!user || !user.groups || !Array.isArray(user.groups)) return [];
  
  const userGroups = groups.filter(group => user.groups.includes(group.id));
  const allPermissions = userGroups.reduce((acc, group) => {
    if (group.permissions && Array.isArray(group.permissions)) {
      return [...acc, ...group.permissions];
    }
    return acc;
  }, []);
  
  // Remove duplicates and return
  return [...new Set(allPermissions)];
};

export const filterUsers = (users, filters) => {
  return users.filter(user => {
    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const matchesEmail = user.email.toLowerCase().includes(searchLower);
      const matchesName = user.displayName.toLowerCase().includes(searchLower);
      if (!matchesEmail && !matchesName) return false;
    }
    
    // Status filter
    if (filters.status === 'active' && !user.isActive) return false;
    if (filters.status === 'inactive' && user.isActive) return false;
    
    // Groups filter
    if (filters.groups && filters.groups.length > 0) {
      const hasMatchingGroup = filters.groups.some(groupId => 
        user.groups && user.groups.includes(groupId)
      );
      if (!hasMatchingGroup) return false;
    }
    
    return true;
  });
};

export const filterGroups = (groups, filters) => {
  return groups.filter(group => {
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const matchesName = group.name.toLowerCase().includes(searchLower);
      const matchesDescription = group.description && 
        group.description.toLowerCase().includes(searchLower);
      if (!matchesName && !matchesDescription) return false;
    }
    
    return true;
  });
};

export const filterPermissions = (permissions, filters) => {
  return permissions.filter(permission => {
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const matchesKey = permission.key.toLowerCase().includes(searchLower);
      const matchesDescription = permission.description && 
        permission.description.toLowerCase().includes(searchLower);
      if (!matchesKey && !matchesDescription) return false;
    }
    
    return true;
  });
};

export const sortData = (data, sortConfig) => {
  if (!sortConfig || !sortConfig.key) return data;
  
  return [...data].sort((a, b) => {
    const aVal = a[sortConfig.key];
    const bVal = b[sortConfig.key];
    
    if (aVal === null || aVal === undefined) return 1;
    if (bVal === null || bVal === undefined) return -1;
    
    let comparison = 0;
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      comparison = aVal.toLowerCase().localeCompare(bVal.toLowerCase());
    } else if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
      comparison = aVal === bVal ? 0 : aVal ? -1 : 1;
    } else {
      comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    }
    
    return sortConfig.direction === 'desc' ? -comparison : comparison;
  });
};

export const paginateData = (data, paginationConfig) => {
  const { page, pageSize } = paginationConfig;
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  return data.slice(startIndex, endIndex);
};

export const getInitialFilters = (type) => {
  switch (type) {
    case 'users':
      return { search: '', status: 'all', groups: [] };
    case 'groups':
      return { search: '' };
    case 'permissions':
      return { search: '' };
    default:
      return {};
  }
};

export const getInitialSort = (type) => {
  switch (type) {
    case 'users':
      return { key: 'displayName', direction: 'asc' };
    case 'groups':
      return { key: 'name', direction: 'asc' };
    case 'permissions':
      return { key: 'key', direction: 'asc' };
    default:
      return { key: '', direction: 'asc' };
  }
};

export const getInitialPagination = () => {
  return { page: 1, pageSize: 10, total: 0 };
};
